import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import './EngagementForm.css';

const emptyResource = (type) => ({
  resource_type: type,
  emp_id: '',
  engagement_name: '',
  skill: '',
  billing_rate: type === 'Engaged' ? '' : 0,
  remarks: ''
});

export default function EngagementForm() {
  const { rfp } = useParams();
  const navigate = useNavigate();
  const isEdit = !!rfp;
  const rfpLabel = rfp ? decodeURIComponent(rfp) : '';

  const [loadingEngagement, setLoadingEngagement] = useState(isEdit);

  const [form, setForm] = useState({
    rfp_num: '',
    sow_id: '',
    msa_id: '',
    customer_id: '',
    from_date: '',
    to_date: '',
    remarks: '',
    status: 'Draft'
  });
  const [resources, setResources] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [msas, setMsas] = useState([]);
  const [sows, setSows] = useState([]);
  const [employees, setEmployees] = useState([]);

  const locked =
    loadingEngagement ||
    form.status === 'Submitted' ||
    form.status === 'Approved' ||
    form.status === 'Active';

  useEffect(() => {
    let mounted = true;
    if (isEdit) setLoadingEngagement(true);

    Promise.all([
      api.get('/customers').then(rows => rows || []).catch(() => []),
      api.get('/msa').then(rows => rows || []).catch(() => []),
      api.get('/sow').then(rows => rows || []).catch(() => []),
      api.get('/employees').then(rows => rows || []).catch(() => [])
    ]).then(([cust, msaRows, sowRows, emp]) => {
      if (!mounted) return;
      setCustomers(cust);
      setMsas(msaRows);
      setSows(sowRows);
      setEmployees(emp);
    });

    if (!isEdit) {
      return () => {
        mounted = false;
      };
    }

    api
      .get(`/engagements/${encodeURIComponent(rfp)}`)
      .then(e => {
        if (!mounted) return;
        setForm({
          rfp_num: e.rfp_num,
          sow_id: e.sow_id,
          msa_id: e.msa_id || '',
          customer_id: e.customer_id || '',
          from_date: e.from_date || '',
          to_date: e.to_date || '',
          remarks: e.remarks || '',
          status: e.status || 'Draft'
        });
        setResources((e.resources || []).map(r => ({
          resource_type: r.resource_type,
          emp_id: r.emp_id,
          engagement_name: r.engagement_name || '',
          skill: r.skill || '',
          billing_rate: r.billing_rate ?? 0,
          remarks: r.remarks || ''
        })));
      })
      .catch(() => {
        if (!mounted) return;
        alert('Could not load this engagement.');
        navigate('/engagements');
      })
      .finally(() => {
        if (mounted) setLoadingEngagement(false);
      });

    return () => {
      mounted = false;
    };
  }, [rfp, isEdit, navigate]);

  const empById = useMemo(() => {
    const m = {};
    employees.forEach(e => { m[e.emp_id] = e; });
    return m;
  }, [employees]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSelectCustomer = (customer_id) => {
    setForm(f => ({ ...f, customer_id, msa_id: '', sow_id: '' }));
  };

  const onSelectMsa = (msa_id) => {
    setForm(f => ({ ...f, msa_id, sow_id: '' }));
  };

  const onSelectSow = (sow_id) => {
    const s = sows.find(x => x.sow_id === sow_id);
    setForm(f => ({ ...f, sow_id, msa_id: s?.msa_id || f.msa_id, customer_id: s?.customer_id || f.customer_id }));
  };

  const filteredMsas = form.customer_id ? msas.filter(m => m.customer_id === form.customer_id) : [];
  const filteredSows = form.msa_id ? sows.filter(s => s.msa_id === form.msa_id) : [];

  const addResource = (type) => setResources(r => [...r, emptyResource(type)]);
  const removeResource = (i) => setResources(r => r.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => setResources(r => r.map((row, idx) => {
    if (idx !== i) return row;
    const next = { ...row, [k]: v };
    if (k === 'emp_id') {
      const emp = empById[v];
      if (emp) {
        if (row.resource_type === 'Engaged') {
          if (!row.engagement_name || row.engagement_name === empById[row.emp_id]?.full_name) {
            next.engagement_name = emp.full_name;
          }
        } else {
          next.engagement_name = emp.full_name;
        }
        if (!row.skill) next.skill = emp.department || '';
      }
    }
    return next;
  }));

  const handleSave = async (afterSave) => {
    if (!form.sow_id) { alert('Please select an SOW'); return; }
    const engaged = resources.filter(r => r.resource_type === 'Engaged');
    if (engaged.length === 0) { alert('At least one Engaged Employee is required'); return; }
    const payload = {
      ...form,
      resources: resources.map(r => ({
        ...r,
        billing_rate: r.resource_type === 'Engaged' ? Number(r.billing_rate) || 0 : 0
      }))
    };
    try {
      let savedRfp = form.rfp_num;
      if (isEdit) {
        const res = await api.put(`/engagements/${encodeURIComponent(rfp)}`, payload);
        savedRfp = res.rfp_num;
      } else {
        const res = await api.post('/engagements', payload);
        savedRfp = res.rfp_num;
      }
      if (afterSave) await afterSave(savedRfp);
      else navigate('/engagements');
    } catch (e) {
      alert(e.message || 'Save failed');
    }
  };

  const handleSubmitForApproval = () => handleSave(async (savedRfp) => {
    await api.post('/approvals/submit', { entity_type: 'Engagement', entity_id: savedRfp });
    navigate('/engagements');
  });

  const renderResourceTable = (type, title) => {
    const isEngaged = type === 'Engaged';
    const rows = resources.map((r, i) => ({ r, i })).filter(x => x.r.resource_type === type);
    return (
      <div className="card engagement-form-resource-card">
        <div className="card-header engagement-form-resource-card-header">
          <span>{title}</span>
          {!locked && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => addResource(type)}>
              + Add
            </button>
          )}
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Employee ID</th>
                <th>Employee Name</th>
                {isEngaged && <th>Engagement Name</th>}
                <th>Designation</th>
                <th>Skill</th>
                {isEngaged && <th>Billing Rate</th>}
                <th>Remarks</th>
                {!locked && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ r, i }) => {
                const emp = empById[r.emp_id];
                return (
                  <tr key={i}>
                    <td>{form.from_date}</td>
                    <td>{form.to_date}</td>
                    <td>
                      <select value={r.emp_id} onChange={e => setRow(i, 'emp_id', e.target.value)} disabled={locked}>
                        <option value="">-- Select --</option>
                        {employees.map(emp => <option key={emp.emp_id} value={emp.emp_id}>{emp.emp_id}</option>)}
                      </select>
                    </td>
                    <td>{emp?.full_name || ''}</td>
                    {isEngaged && (
                      <td>
                        <input value={r.engagement_name} onChange={e => setRow(i, 'engagement_name', e.target.value)} disabled={locked} />
                      </td>
                    )}
                    <td>{emp?.designation || ''}</td>
                    <td>
                      <input value={r.skill || ''} onChange={e => setRow(i, 'skill', e.target.value)} disabled={locked} />
                    </td>
                    {isEngaged && (
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="engagement-form-rate-input"
                          value={r.billing_rate}
                          onChange={e => setRow(i, 'billing_rate', e.target.value)}
                          disabled={locked}
                        />
                      </td>
                    )}
                    <td>
                      <input value={r.remarks || ''} onChange={e => setRow(i, 'remarks', e.target.value)} disabled={locked} />
                    </td>
                    {!locked && (
                      <td>
                        <button
                          type="button"
                          className="engagement-form-remove-btn"
                          title="Remove row"
                          aria-label="Remove row"
                          onClick={() => removeResource(i)}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={isEngaged ? 10 : 8} className="engagement-form-empty-placeholder">
                    No {type === 'Engaged' ? 'engaged' : 'support'} employees added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const titleRfp = form.rfp_num || rfpLabel;

  return (
    <>
      <div className="page-header">
        <h1>{isEdit ? `Engagement ${titleRfp}` : 'New Engagement'}</h1>
        <div className="btn-group">
          {!locked && <button type="button" className="btn btn-primary" onClick={() => handleSave()}>Save</button>}
          {!locked && (form.status === 'Draft' || form.status === 'Rejected') && isEdit && (
            <button type="button" className="btn btn-success" onClick={handleSubmitForApproval}>
              Submit for Approval
            </button>
          )}
        </div>
      </div>
      <div className="page-content engagement-form-page">
        <nav className="page-back-nav" aria-label="Back navigation">
          <Link to="/engagements" className="page-back-link">
            ← Back to Engagements
          </Link>
        </nav>
        {loadingEngagement && isEdit ? (
          <div className="card engagement-form-loading" role="status" aria-live="polite">
            <div className="engagement-form-loading-spinner" aria-hidden />
            <p>Loading engagement {rfpLabel}…</p>
          </div>
        ) : (
          <>
        <div className="card">
          <div className="card-header">Engagement Details</div>
          <div className="form-grid">
            <div className="form-group">
              <label>Client *</label>
              <select value={form.customer_id} onChange={e => onSelectCustomer(e.target.value)} disabled={locked || isEdit}>
                <option value="">-- Select Customer --</option>
                {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.legal_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>MSA *</label>
              <select value={form.msa_id} onChange={e => onSelectMsa(e.target.value)} disabled={locked || isEdit || !form.customer_id}>
                <option value="">{form.customer_id ? '-- Select MSA --' : '-- Select customer first --'}</option>
                {filteredMsas.map(m => <option key={m.msa_id} value={m.msa_id}>{m.msa_id}{m.msa_ref ? ` (${m.msa_ref})` : ''}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>SOW *</label>
              <select value={form.sow_id} onChange={e => onSelectSow(e.target.value)} disabled={locked || isEdit || !form.msa_id}>
                <option value="">{form.msa_id ? '-- Select SOW --' : '-- Select MSA first --'}</option>
                {filteredSows.map(s => <option key={s.sow_id} value={s.sow_id}>{s.sow_id}{s.sow_ref ? ` (${s.sow_ref})` : ''}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>RFP Number {isEdit ? '(editable; must be unique)' : '(auto-generated)'}</label>
              <input value={form.rfp_num} onChange={e => set('rfp_num', e.target.value)} placeholder={isEdit ? '' : 'Auto on save'} disabled={locked} />
            </div>
            <div className="form-group">
              <label>From Date</label>
              <input type="date" value={form.from_date} onChange={e => set('from_date', e.target.value)} disabled={locked} />
            </div>
            <div className="form-group">
              <label>To Date</label>
              <input type="date" value={form.to_date} onChange={e => set('to_date', e.target.value)} disabled={locked} />
            </div>
            <div className="form-group">
              <label>Status</label>
              <input value={form.status} disabled />
            </div>
            <div className="form-group full-width">
              <label>Remarks</label>
              <textarea rows={2} value={form.remarks} onChange={e => set('remarks', e.target.value)} disabled={locked} />
            </div>
          </div>
        </div>

        {renderResourceTable('Engaged', 'Engaged Employees')}
        {renderResourceTable('Support', 'Support / Backup Employees')}
          </>
        )}
      </div>
    </>
  );
}
